import { insightArticles } from "./content.js";
import { assetUrl } from "./asset-url.js";

const asset = (name) => (
  name.endsWith(".png")
    ? assetUrl(`/assets/optimized/${name.replace(/\.png$/i, ".webp")}`)
    : assetUrl(`/assets/${name}`)
);

export const science = [
  {
    slug: "3d-million-base-dna-synthesis",
    title: "3D Million-base DNA Synthesis",
    short: "Structured, traceable concepts for constructing very large DNA architectures.",
    description:
      "A platform concept for assembling very large, designed DNA architectures while maintaining identity, integrity, and modular quality control.",
    icon: asset("science_icon_svg_007_dna_helix.svg"),
    image: asset("science-dna.png"),
    points: ["Structured design", "Modular quality nodes", "Version traceability"],
  },
  {
    slug: "umbert-genome-assembly",
    title: "UMBERT Genome Assembly",
    short: "Context-aware genome assembly with explicit uncertainty and review.",
    description:
      "An intelligent assembly framework for organizing complex genomic evidence into interpretable, quality-aware genome models.",
    icon: asset("science_icon_svg_005_circuit_petri_dish.svg"),
    image: asset("lab-data.png"),
    points: ["Evidence ingestion", "Assembly graph", "Confidence layer"],
  },
  {
    slug: "artificial-delivery-vehicle-protein",
    title: "Artificial Delivery Vehicle Protein",
    short: "Engineered protein concepts for controlled, modular delivery research.",
    description:
      "A research platform for exploring modular protein vehicles while keeping payload, sequence, and delivery parameters within controlled programs.",
    icon: asset("science_icon_svg_001_antibody.svg"),
    image: asset("cells.png"),
    points: ["Modular architecture", "Controlled evaluation", "Program-specific evidence"],
  },
  {
    slug: "direct-molecular-counting",
    title: "Direct Molecular Counting",
    short: "Precision measurement concepts for direct, quality-aware molecular signals.",
    description:
      "An assay framework for turning single-molecule events into interpretable measurements with calibration, quality control, and traceable review.",
    icon: asset("science_icon_svg_019_signal_bars.svg"),
    image: asset("microfluidic.png"),
    points: ["Direct signal", "Calibration framework", "Quality-aware outputs"],
  },
];

export const platform = [
  {
    slug: "programmable-life-design-core-ai-engine",
    title: "Programmable Life Design Core AI Engine",
    short: "Design intelligence connecting biological evidence, candidate generation, and review.",
    description:
      "A design layer that brings data, biological context, candidate generation, and confidence-aware review into one governed workflow.",
    icon: asset("science_icon_svg_003_brain_circuit.svg"),
    image: asset("lab-data.png"),
    points: ["Context models", "Candidate ranking", "Confidence-aware review"],
  },
  {
    slug: "synthetic-epigenome-architecture",
    title: "Synthetic Epigenome Architecture",
    short: "A programmable layer for exploring biological state and regulation.",
    description:
      "A research architecture for studying epigenomic state spaces, regulatory relationships, and program-specific design hypotheses.",
    icon: asset("science_icon_svg_006_dna_curve.svg"),
    image: asset("science-dna.png"),
    points: ["State architecture", "Regulatory context", "Controlled hypotheses"],
  },
  {
    slug: "programmable-life-surrogate",
    title: "Programmable Life Surrogate",
    short: "Surrogate systems for simulation, comparison, and uncertainty-aware decisions.",
    description:
      "A digital surrogate framework for comparing trajectories, testing assumptions, and making uncertainty visible before translation.",
    icon: asset("science_icon_svg_014_network_triangle.svg"),
    image: asset("insight-dna.png"),
    points: ["Digital surrogates", "Simulation trajectories", "Visible uncertainty"],
  },
  {
    slug: "precision-assays",
    title: "Precision Assays",
    short: "Measurement systems designed to close the loop between design and evidence.",
    description:
      "Assay concepts that connect micro-scale events, calibration, quality control, and governed evidence back to platform decisions.",
    icon: asset("science_icon_svg_017_sample_analysis.svg"),
    image: asset("platform-assay.png"),
    points: ["Assay design", "Calibration", "Evidence feedback"],
  },
];

export const applications = [
  {
    slug: "precision-oncology",
    title: "Precision Oncology",
    short: "Platform capabilities supporting biomarker research and precision measurement.",
    description:
      "Research concepts connecting molecular signals, governed assays, and interpretable evidence for oncology programs.",
    icon: asset("science_icon_svg_015_petri_dish.svg"),
    image: asset("application-oncology.png"),
    points: ["Biomarker research", "Precision measurement", "Evidence governance"],
  },
  {
    slug: "industrial-resilience",
    title: "Industrial Resilience",
    short: "Biological systems and measurement concepts for resilient production environments.",
    description:
      "A program space spanning bioprocess observation, material interfaces, and evidence-led operational research.",
    icon: asset("science_icon_svg_002_bioreactor.svg"),
    image: asset("application-industrial.png"),
    points: ["Process observation", "Material interfaces", "Operational evidence"],
  },
  {
    slug: "biodefense-cbrn-threat",
    title: "Biodefense and CBRN Threat",
    short: "Preparedness research centered on detection, evidence, and controlled access.",
    description:
      "A non-operational research area for governed detection concepts, readiness workflows, and responsible evidence access.",
    icon: asset("science_icon_svg_018_shield_check.svg"),
    image: asset("lab-branded.png"),
    points: ["Detection concepts", "Preparedness workflows", "Controlled access"],
  },
  {
    slug: "longevity",
    title: "Longevity",
    short: "Research programs exploring cellular state, resilience, and healthy-aging biology.",
    description:
      "A platform-led research area connecting cellular, immune, and microbiome evidence without making life-extension claims.",
    icon: asset("science_icon_svg_010_heartbeat.svg"),
    image: asset("cells.png"),
    points: ["Cellular state", "Systems resilience", "Healthy-aging research"],
  },
];

export const products = [
  { slug: "lungdefend", title: "LungDefend", area: "Precision Oncology", status: "Product concept", icon: "17", short: "A governed product concept aligned to precision-oncology research workflows." },
  { slug: "bremony", title: "Bremony", area: "Precision Oncology", status: "Product concept", icon: "15", short: "A product concept presented with region- and evidence-specific status." },
  { slug: "prosglad", title: "ProsGlad", area: "Precision Oncology", status: "Product concept", icon: "01", short: "A precision-oncology product concept with controlled claims and evidence review." },
  { slug: "detedx", title: "DeteDx", area: "Precision Oncology", status: "Product concept", icon: "19", short: "A detection-oriented product concept connected to governed assay evidence." },
  { slug: "cololite-plus", title: "ColoLite+", area: "Longevity", status: "Product concept", icon: "09", short: "A longevity research product concept; availability and evidence vary by region." },
  { slug: "rynvetha", title: "Rynvetha", area: "Biodefense and CBRN Threat", status: "Research concept", icon: "18", short: "A controlled-access research concept for preparedness and detection workflows." },
  { slug: "ulmyxen", title: "Ulmyxen", area: "Biodefense and CBRN Threat", status: "Research concept", icon: "20", short: "A program-linked research concept subject to scientific and regulatory review." },
  { slug: "bioweave-buildup-film", title: "BioWeave Buildup Film", area: "Industrial Resilience", status: "Research concept", icon: "08", short: "A materials-focused concept for industrial biological interfaces." },
  { slug: "opticull-x", title: "OptiCull X", area: "Industrial Resilience", status: "Research concept", icon: "16", short: "An industrial research concept with evidence-led status and use boundaries." },
].map((item) => ({
  ...item,
  description: item.short,
  image: item.area === "Industrial Resilience" ? asset("application-industrial.png") : asset("chemistry-lab.png"),
  icon: asset(`science_icon_svg_0${item.icon}_${iconName(item.icon)}.svg`),
  points: ["Status transparency", "Evidence-linked content", "Region-specific review"],
}));

export const pipeline = [
  {
    slug: "tolerivex",
    title: "Tolerivex",
    area: "Longevity",
    status: "Research program",
    short: "Exploring tolerance and resilience pathways within governed research programs.",
    icon: asset("science_icon_svg_009_gut_microbiome.svg"),
  },
  {
    slug: "methuselon-969",
    title: "Methuselon 969",
    area: "Longevity",
    status: "Research program",
    short: "A platform research program focused on cellular resilience and evidence development.",
    icon: asset("science_icon_svg_010_heartbeat.svg"),
  },
  {
    slug: "bioweave-buildup-film",
    title: "BioWeave Buildup Film",
    area: "Industrial Resilience",
    status: "Research program",
    short: "A material-interface research program for industrial biological systems.",
    icon: asset("science_icon_svg_008_gene_edit_signal.svg"),
  },
  {
    slug: "aquila-sentinel",
    title: "Aquila Sentinel",
    area: "Biodefense and CBRN Threat",
    status: "Research program",
    short: "A controlled preparedness research program centered on detection and review.",
    icon: asset("science_icon_svg_018_shield_check.svg"),
  },
].map((item) => ({
  ...item,
  description: item.short,
  image: asset("hero-data-wave.png"),
  points: ["Research-stage transparency", "Evidence review", "Controlled access"],
}));

export const groups = {
  science: {
    title: "Science at the core.",
    lead: "Four foundational technologies form Medein's scientific capability.",
    description:
      "Large-scale DNA construction, intelligent genome assembly, engineered delivery proteins, and direct molecular counting connect through one evidence-led science layer.",
    items: science,
    image: asset("science-dna.png"),
  },
  platform: {
    title: "A platform for programmable biology.",
    lead: "Design intelligence, epigenome architecture, surrogate systems, and precision assays.",
    description:
      "Medein connects biological context, computation, simulation, and measurement in a governed architecture built for translation.",
    items: platform,
    image: asset("platform-assay.png"),
  },
  applications: {
    title: "From platform to application.",
    lead: "One platform capability system, focused across four application areas.",
    description:
      "Each application area presents only evidence-backed, status-aware content and keeps research concepts distinct from approved products.",
    items: applications,
    image: asset("application-oncology.png"),
  },
  "product-line": {
    title: "A growing product system.",
    lead: "Products and research concepts organized by application, evidence, and status.",
    description:
      "Medein separates product information from pipeline programs so that availability, research stage, and evidence remain clear.",
    items: products,
    image: asset("chemistry-lab.png"),
  },
  pipeline: {
    title: "Traceable progress.",
    lead: "Four research programs presented through evidence, milestones, and responsible access.",
    description:
      "Program pages avoid implied success rates and clinical claims, focusing instead on transparent status and review.",
    items: pipeline,
    image: asset("hero-data-wave.png"),
  },
};

export const insights = [
  {
    category: "Design",
    title: "Designing with biological complexity",
    summary: "How programmable, context-aware systems help teams work with biological complexity.",
    image: asset("insights-team.png"),
  },
  {
    category: "Evidence",
    title: "Evidence frameworks for programmable life",
    summary: "Making evidence level, review date, and access status visible by design.",
    image: asset("insight-dna.png"),
  },
  {
    category: "Systems",
    title: "From platform architecture to product systems",
    summary: "How shared interfaces connect science, translation, and product development.",
    image: asset("lab-branded.png"),
  },
];

export const searchableRoutes = [
  ...Object.entries(groups).flatMap(([base, group]) => [
    { title: group.title, path: `/${base}`, kind: "Overview" },
    ...group.items.map((item) => ({ title: item.title, path: `/${base}/${item.slug}`, kind: base })),
  ]),
  { title: "Insights", path: "/insights", kind: "Content" },
  { title: "Articles", path: "/insights/articles", kind: "Insights" },
  { title: "Resources", path: "/insights/resources", kind: "Insights" },
  { title: "Events", path: "/insights/events", kind: "Insights" },
  ...insightArticles.map((article) => ({
    title: article.title,
    path: `/insights/articles/${article.slug}`,
    kind: article.category,
  })),
  { title: "About Medein", path: "/about", kind: "Company" },
  { title: "Our Story", path: "/about/story", kind: "Company" },
  { title: "Leadership", path: "/about/leadership", kind: "Company" },
  { title: "Careers", path: "/careers", kind: "Company" },
  { title: "Investors", path: "/investors", kind: "Company" },
  { title: "Partnership", path: "/partnership", kind: "Company" },
  { title: "Contact", path: "/contact", kind: "Company" },
];

function iconName(number) {
  return {
    "01": "antibody",
    "08": "gene_edit_signal",
    "09": "gut_microbiome",
    "11": "lab_glassware",
    "15": "petri_dish",
    "16": "robotic_arm",
    "17": "sample_analysis",
    "18": "shield_check",
    "19": "signal_bars",
    "20": "target",
  }[number];
}
